import React, { useState, useEffect } from 'react';
import { http } from '../../lib/http';
import { todayToronto } from '../../lib/time'; // Issue #3 FIX


// Income & Expenses Tab Component
const IncomeExpensesTab = ({ setMessage, setError }) => {
    // Expense state
    const [expenseCategories, setExpenseCategories] = useState([]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [showAddCategory, setShowAddCategory] = useState(false);
    const [expenseAmount, setExpenseAmount] = useState('');
    const [expenseDate, setExpenseDate] = useState(todayToronto()); // Issue #3 FIX: Use Toronto timezone
    const [paymentMethod, setPaymentMethod] = useState('');
    const [expenseDescription, setExpenseDescription] = useState('');
    const [recordingExpense, setRecordingExpense] = useState(false);
    const [expenseTransactions, setExpenseTransactions] = useState([]);
    const [expenseFilterDate, setExpenseFilterDate] = useState('');

    // Load expense categories and transactions
    useEffect(() => {
        loadExpenseCategories();
        loadExpenseTransactions();
    }, []);


    const loadExpenseCategories = async () => {
        try {
            const response = await http.get('/admin/expense/categories');
            if (response.data.success) {
                setExpenseCategories(response.data.data?.categories || []);
            }
        } catch (error) {
            console.error('Failed to load expense categories:', error);
            setError('Failed to load expense categories');
        }
    };

    const addNewCategory = async () => {
        if (!newCategoryName.trim()) {
            setError('Category name is required');
            return;
        }

        try {
            const response = await http.post('/admin/expense/categories', {
                name: newCategoryName.trim()
            });

            if (response.data.success) {
                setMessage('Category created successfully');
                setNewCategoryName('');
                setShowAddCategory(false);
                loadExpenseCategories();
            }
        } catch (error) {
            console.error('Failed to add category:', error);
            setError(error.response?.data?.detail || 'Failed to add category');
        }
    };

    const recordExpense = async () => {
        if (!selectedCategory || !expenseAmount || !expenseDate) {
            setError('Please fill in all required fields');
            return;
        }

        try {
            setRecordingExpense(true);
            const response = await http.post('/admin/expense/record', {
                category: selectedCategory,
                amount: parseFloat(expenseAmount),
                expense_date: expenseDate,
                payment_method: paymentMethod,
                description: expenseDescription
            });

            if (response.data.success) {
                setMessage('Expense recorded successfully');
                setSelectedCategory('');
                setExpenseAmount('');
                setPaymentMethod('');
                setExpenseDescription('');
                loadExpenseTransactions();
            }
        } catch (error) {
            console.error('Failed to record expense:', error);
            setError(error.response?.data?.detail || 'Failed to record expense');
        } finally {
            setRecordingExpense(false);
        }
    };

    const loadExpenseTransactions = async () => {
        try {
            const params = {};
            if (expenseFilterDate) {
                params.start_date = expenseFilterDate;
                params.end_date = expenseFilterDate;
            }

            const response = await http.get('/admin/expense/transactions', { params });
            if (response.data.success) {
                setExpenseTransactions(response.data.data?.expenses || []);
            }
        } catch (error) {
            console.error('Failed to load expense transactions:', error);
        }
    };

    const exportExpenseCSV = () => {
        if (expenseTransactions.length === 0) {
            setError('No expense data to export');
            return;
        }

        const headers = ['Date', 'Category', 'Amount', 'Payment Method', 'Description'];
        const rows = expenseTransactions.map(e => [
            e.expense_date,
            e.category,
            `$${e.amount}`,
            e.payment_method || '',
            e.description
        ]);

        const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `expenses_${todayToronto()}.csv`; // Issue #3 FIX: Use Toronto timezone
        a.click();
    };


    const deleteExpenseTransaction = async (expenseId) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) {
            return;
        }

        try {
            const response = await http.delete(`/admin/expense/transactions/${expenseId}`);
            if (response.data.success) {
                setMessage('Expense deleted successfully');
                loadExpenseTransactions();
            }
        } catch (error) {
            console.error('Failed to delete expense:', error);
            setError(error.response?.data?.detail || 'Failed to delete expense');
        }
    };


    return (
        <div className="space-y-6">
            {/* Expense Section */}
            <div className="space-y-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left: Record Expense */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3">
                            <h3 className="text-xl font-bold text-white">Record Expense</h3>
                        </div>
                        <div className="p-6">

                            <div className="space-y-4">
                                {/* Expense Category */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Expense Category</label>
                                    {!showAddCategory ? (
                                        <div className="flex space-x-2">
                                            <select
                                                value={selectedCategory}
                                                onChange={(e) => setSelectedCategory(e.target.value)}
                                                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500 focus:outline-none"
                                            >
                                                <option value="">-- Select Category --</option>
                                                {expenseCategories.map(cat => (
                                                    <option key={cat.category_id} value={cat.name}>{cat.name}</option>
                                                ))}
                                            </select>
                                            <button
                                                onClick={() => setShowAddCategory(true)}
                                                className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-md whitespace-nowrap"
                                            >
                                                + New
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex space-x-2">
                                            <input
                                                type="text"
                                                value={newCategoryName}
                                                onChange={(e) => setNewCategoryName(e.target.value)}
                                                placeholder="Enter category name"
                                                className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500 focus:outline-none"
                                            />
                                            <button
                                                onClick={addNewCategory}
                                                className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md"
                                            >
                                                Add
                                            </button>
                                            <button
                                                onClick={() => {
                                                    setShowAddCategory(false);
                                                    setNewCategoryName('');
                                                }}
                                                className="px-4 py-2 bg-gray-500 hover:bg-white border border-gray-300 text-gray-900 rounded-md"
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Expense Amount */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Amount ($) *</label>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={expenseAmount}
                                        onChange={(e) => setExpenseAmount(e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500 focus:outline-none"
                                        placeholder="0.00"
                                    />
                                </div>

                                {/* Expense Date */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Expense Date *</label>
                                    <input
                                        type="date"
                                        value={expenseDate}
                                        onChange={(e) => setExpenseDate(e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500 focus:outline-none"
                                    />
                                </div>

                                {/* Payment Method (Optional) */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Payment Method (Optional)</label>
                                    <select
                                        value={paymentMethod}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500 focus:outline-none"
                                    >
                                        <option value="">-- Select --</option>
                                        <option value="Cash">Cash</option>
                                        <option value="E-Transfer">E-Transfer</option>
                                        <option value="Credit Card">Credit Card</option>
                                        <option value="Debit Card">Debit Card</option>
                                        <option value="Bank Transfer">Bank Transfer</option>
                                    </select>
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-2">Description</label>
                                    <textarea
                                        value={expenseDescription}
                                        onChange={(e) => setExpenseDescription(e.target.value)}
                                        rows={3}
                                        className="w-full px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500 focus:outline-none"
                                        placeholder="Enter expense description..."
                                    />
                                </div>

                                {/* Record Button */}
                                <button
                                    onClick={recordExpense}
                                    disabled={recordingExpense}
                                    className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-md disabled:opacity-50"
                                >
                                    {recordingExpense ? 'Recording...' : 'Record Expense'}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Right: Expense Transaction History */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden">
                        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-3 flex justify-between items-center">
                            <h3 className="text-xl font-bold text-white">Expense History</h3>
                            <button
                                onClick={exportExpenseCSV}
                                className="px-4 py-2 bg-white hover:bg-gray-100 text-orange-500 rounded-md text-sm font-medium"
                            >
                                Export CSV
                            </button>
                        </div>
                        <div className="p-6">
                            {/* Date Filter */}
                            <div className="mb-4">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Filter by Date</label>
                                <div className="flex gap-2">
                                    <input
                                        type="date"
                                        value={expenseFilterDate}
                                        onChange={(e) => {
                                            setExpenseFilterDate(e.target.value);
                                            setTimeout(loadExpenseTransactions, 100);
                                        }}
                                        className="flex-1 px-3 py-2 border-2 border-gray-300 rounded-md focus:border-orange-500"
                                    />
                                    <button
                                        onClick={() => {
                                            setExpenseFilterDate('');
                                            setTimeout(loadExpenseTransactions, 100);
                                        }}
                                        className="px-4 py-2 bg-gray-500 hover:bg-white border border-gray-300 text-gray-900 rounded-md whitespace-nowrap"
                                    >
                                        Clear
                                    </button>
                                </div>
                            </div>

                            {/* Transactions Table */}
                            <div className="overflow-y-auto max-h-96">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900">Date</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900">Category</th>
                                            <th className="px-3 py-2 text-right text-xs font-semibold text-gray-900">Amount</th>
                                            <th className="px-3 py-2 text-left text-xs font-semibold text-gray-900">Method</th>
                                            <th className="px-3 py-2 text-center text-xs font-semibold text-gray-900">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {expenseTransactions.map((expense) => (
                                            <tr key={expense._id || expense.id}>
                                                <td className="px-3 py-2 text-sm text-gray-900">{expense.expense_date}</td>
                                                <td className="px-3 py-2 text-sm text-gray-900">{expense.category}</td>
                                                <td className="px-3 py-2 text-sm text-right font-semibold text-red-600">${expense.amount}</td>
                                                <td className="px-3 py-2 text-sm text-gray-900">{expense.method || expense.payment_method || '-'}</td>
                                                <td className="px-3 py-2 text-center">
                                                    <button
                                                        onClick={() => deleteExpenseTransaction(expense.id || expense._id)}
                                                        className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs rounded"
                                                    >
                                                        Delete
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                                {expenseTransactions.length === 0 && (
                                    <p className="text-center text-gray-500 py-4">No expense transactions found</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IncomeExpensesTab;
